import React, { useEffect, useState } from 'react';
import OrganizationContainer from '../../../../../shared/state/containers/OrganizationContainer';
import ApplicationContainer from '../../../../../shared/state/containers/ApplicationContainer';
import AuthContainer from '../../../../../shared/state/containers/AuthContainer';
import { Redirect } from 'react-router';
import SessionAnalyticsContainer from '../../../../../shared/hooks/SessionAnalyticsContainer';
import HeaderBar from '../../../../../shared/components/structure/HeaderBar';
import Logo from '../../../../../shared/scenes/dashboardHeader/components/Logo';
import DashboardHeader from '../../../../../shared/scenes/dashboardHeader';
import TitleStateContainer from '../../../../../shared/state/containers/TitleStateContainer';
import useIsMobile from '../../../../../shared/hooks/UseIsMobile';
import styled from '@emotion/styled';
import { color_colors_light_ocean, color_shades_dark, color_shades_darkest, color_shades_lighter, greeny_blue, pastel_green, pastel_yellow } from '../../../../../shared/constants/colors';
import moment from 'moment';
import { REVIEW_SESSION_DATE_FORMAT } from '../../../../../shared/models/analytics/AnalyticsDateFormat';
import Loader from '../../../../../shared/components/general/Loader';
import CustomScrollbars from '../../../../../shared/components/structure/CustomScrollbars';
import useWindowSize from '../../../../../shared/hooks/UseWindowSize';
import qs from 'query-string';
import SessionReview from '../../../../../shared/scenes/applicationAnalytics/components/SessionReview';
import BackButton from '../../../../../shared/components/general/BackButton';
import { SessionListModel } from '../../../../../shared/hooks/SessionAnalyticsContainer';
import SearchField from '../../../../../shared/components/forms/SearchField';
import ConversationMarkdown from '../../../../../shared/scenes/applicationAnalytics/components/ConversationMarkdown';
import { css } from '@emotion/css';
import TextField from '../../../../../shared/components/forms/TextField';
import Button from '../../../../../shared/components/general/Button';
import PageError from '../../../../../shared/components/general/PageError';
import { VOICIFY_ACCESS_TOKEN_STORE } from '../../../../../shared/constants/Keys';
import CurrentApplicationContainer from '../../../../../shared/hooks/CurrentApplicationContainer';

const expandIcon = require('../../../../../shared/content/images/navigation-right.svg');
const phoneIcon = require('../../../../../shared/content/images/phone-thick.svg');

interface ReviewDashboardProps {
    orgsContainer: OrganizationContainer
    appContainer: ApplicationContainer
    authContainer: AuthContainer
    applicationId: string
    history: any
    titleContainer: TitleStateContainer,
}

interface ReviewSessionListModel extends SessionListModel {
    latestSession?: { [key: string]: any }
    orderId?: string,
    color?: string,
    extraText?: string,
    description?: string,
    searchText?: string
}

const STATIC_SUBTRACTION_MAIN = 140;
const STATIC_SUBTRACTION_SESSION = 140;
const STATIC_SUBTRACTION_NO_FOOTER = 80;
const MS_IN_2_HOURS = 2 * 60 * 60 * 1000;

const ReviewDashboardContent: React.FC<ReviewDashboardProps> = (props) => {
    const [redirect, setRedirect] = useState(false);
    const [selectedSession, setSelectedSession] = useState("");
    const [sessionEvents, setSessionEvents] = useState([] as any[]);
    const [loadedSessions, setLoadedSessions] = useState<ReviewSessionListModel[]>([]);
    const [filteredLoadedSessions, setFilteredLoadedSessions] = useState<ReviewSessionListModel[]>([]);
    const [nextSessionIndexToLoad, setNextSessionIndexToLoad] = useState(-1);
    const [searchFieldValue, setSearchFieldValue] = useState("");
    const [filterTransfers, setFilterTransfers] = useState(false);
    const [filterRetransfers, setFilterRetransfers] = useState(true);
    const [filterOrderPlacedTransfers, setFilterOrderPlacedTransfers] = useState(false);
    const [minDurationSeconds, setMinDurationSeconds] = useState(20);
    const [daysBack, setDaysBack] = useState(2);
    const [minConversationTurns, setMinConversationTurns] = useState(4);
    const [key, setKey] = useState("");
    const isMobile = useIsMobile();
    const windowSize = useWindowSize();
    const sessionContainer = SessionAnalyticsContainer.useContainer();
    const currentApplicationContainer = CurrentApplicationContainer.useContainer();
    const [allLoaded, setAllLoaded] = useState(false);
    const [loadingSession, setLoadingSession] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState<string>("");
    const [readyToLoad, setReadyToLoad] = useState(false);

    useEffect(() => {
        currentApplicationContainer.setOrganizationId(
            props.orgsContainer.state.currentOrganization?.id || null
        );
        return () => currentApplicationContainer.clearOrganizationId();
    }, [props.orgsContainer.state.currentOrganization?.id]);

    useEffect(() => {
        if (window.location?.pathname?.toLowerCase()?.includes('/fullreview/')) {
            // we want to show everything for full review
            setFilterTransfers(false);
            setFilterRetransfers(false);
            setFilterOrderPlacedTransfers(false);
            setMinConversationTurns(1);
            setMinDurationSeconds(10);
            setDaysBack(10);
        } else {
            setFilterTransfers(true);
            setFilterRetransfers(false);
            setFilterOrderPlacedTransfers(true);
            setMinConversationTurns(4);
            setMinDurationSeconds(20);
            setDaysBack(2);
        }
        setReadyToLoad(true);
    }, [window.location.pathname])
    useEffect(() => {
        // try to get api key from query string
        const query = qs.parse(window.location.search);
        if (props.authContainer.state.isAuthenticated || props.authContainer.state?.refreshToken?.length) {
            const storedAccessToken = sessionStorage.getItem(VOICIFY_ACCESS_TOKEN_STORE); // don't want to try any api calls till we have session storage. 
            if (!props.authContainer.state.accessToken?.length || !storedAccessToken?.length) return; // retry once we have this. 
            load();
            return;
        } else if (query?.key?.length) {
            setKey(query.key as string);
            setShowPassword(true);
            return;
        } else {
            props.authContainer.signOut();
            setRedirect(true);
        }
    }, [props.authContainer.state.isAuthenticated, props.authContainer.state.accessToken, sessionStorage, daysBack, readyToLoad])

    const load = async () => {
        if (!readyToLoad) return;
        const result = await sessionContainer.loadSessionsForReview(props.applicationId, minDurationSeconds, daysBack);
        if (result !== "Ok") {
            props.authContainer.signOut();
            setRedirect(true);
        }
        setAllLoaded(true);
    }

    useEffect(() => {
        if (selectedSession?.length) {
            props.titleContainer.setTitle('Call Review');
        } else {
            props.titleContainer.setTitle('Recent Calls');
        }
    }, [selectedSession])

    useEffect(() => {
        if (searchFieldValue?.length) {
            var lowered = searchFieldValue.toLowerCase();
            const filteredSessions = loadedSessions.filter((session) => {
                const phoneNumber = session.from;;
                const orderId = session.orderId;
                return phoneNumber.includes(searchFieldValue) ||
                    orderId?.includes(searchFieldValue) ||
                    session.sessionId?.includes(searchFieldValue) ||
                    session?.searchText?.includes(lowered);
            });
            setFilteredLoadedSessions(filteredSessions);
        } else {
            setFilteredLoadedSessions(loadedSessions);
        }
    }, [searchFieldValue, loadedSessions]);

    useEffect(() => {
        if (selectedSession?.length
            && selectedSession in sessionContainer.events
            && (!sessionEvents.length || sessionEvents[0].sessionId !== selectedSession)) {
            const newEvents = sessionContainer.events[selectedSession];
            // sort by event date
            newEvents.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
            setSessionEvents(newEvents);
            setLoadingSession(false);
        }
        if (sessionEvents?.length && sessionEvents[0].sessionId === selectedSession) {
            setLoadingSession(false);
        }
    }, [sessionContainer.events, selectedSession])

    useEffect(() => {
        const query = qs.parse(window.location.search);
        if (query?.sessionId?.length) {
            if (selectedSession === query.sessionId)
                return;
            if (sessionContainer.sessionsForReview.some(s => s.sessionId === query.sessionId as string)) {
                setSelectedSession(query.sessionId as string);
            }
        }
        else {
            setSelectedSession("");
        }
    }, [window.location.search, sessionContainer.sessionsForReview])

    useEffect(() => {
        if (selectedSession.length == 0) return;
        if (sessionEvents.length && sessionEvents[0].sessionId === selectedSession) return;
        sessionContainer.loadEventsForSession(props.applicationId, selectedSession);
        setLoadingSession(true);
    }, [selectedSession]);

    useEffect(() => {
        if (sessionContainer.sessionsForReview?.length &&
            nextSessionIndexToLoad < 0 &&
            sessionContainer.sessionsForReview.some(s => !loadedSessions.some(l => l.sessionId === s.sessionId))) {
            setNextSessionIndexToLoad(0);
        }
    }, [sessionContainer.sessionsForReview])

    useEffect(() => {
        if (nextSessionIndexToLoad < 0) return;
        if (nextSessionIndexToLoad >= sessionContainer.sessionsForReview.length) {
            setNextSessionIndexToLoad(-1);
            return;
        }
        parallelSessionLoader(nextSessionIndexToLoad);
    }, [nextSessionIndexToLoad])

    const login = async () => {
        if (!key?.length) {
            props.authContainer.signOut();
            setRedirect(true);
            return;
        }
        var result = await props.authContainer.signIn(`${key}@review-user.com`, password);
        if (result.resultType === "Ok") {
            setShowPassword(false);
            load();
        } else {
        }
    }
    const parallelSessionLoader = async (idx: number) => {
        const parallelism = 2;
        const tasks = [] as Promise<ReviewSessionListModel>[];
        for (let i = 0; i < parallelism; i++) {
            if (idx >= sessionContainer.sessionsForReview.length) {
                break;
            }
            const session = sessionContainer.sessionsForReview[idx];
            tasks.push(loadSessionWithLatestAttributes(session));
            idx++;
        }
        const results = await Promise.all(tasks);
        const newSessions = results.filter(s => s !== null) as ReviewSessionListModel[];
        setLoadedSessions([...loadedSessions, ...newSessions]);
        setNextSessionIndexToLoad(nextSessionIndexToLoad + parallelism);
    }

    const loadSessionWithLatestAttributes = async (session): Promise<ReviewSessionListModel> => {
        const sessionId = session.sessionId;
        var contentHitTask = sessionContainer.loadLatestRecentContentHitsForSession(props.applicationId, sessionId);
        const orderPlaces = sessionContainer.loadRecentOrderPlacesForSession(sessionId);
        const transfers = sessionContainer.loadRecentTransfersForSession(sessionId);
        var results = await Promise.all([contentHitTask]);
        const contentHits = results[0];
        if (contentHits?.length) {
            var hit = contentHits.find((s) => s.originalRequest?.sessionAttributes?.orderId !== undefined &&
                s.originalRequest?.sessionAttributes?.orderId !== null &&
                `${s.originalRequest?.sessionAttributes?.orderId}`.length);
            if (!hit) {
                hit = contentHits[0]
            }
            const newSessionLoaded = {
                ...session,
                latestSession: hit?.originalRequest?.sessionAttributes
            };
            if (newSessionLoaded?.latestSession?.conversationTurnHistoryState?.TurnHistory?.length
                && newSessionLoaded?.latestSession?.
                    conversationTurnHistoryState?.TurnHistory?.length > minConversationTurns) {
                let add = true;
                if (newSessionLoaded?.latestSession?.orderId !== undefined &&
                    newSessionLoaded?.latestSession?.orderId !== null &&
                    `${newSessionLoaded?.latestSession?.orderId}`.length) {
                    newSessionLoaded.orderId = `${newSessionLoaded?.latestSession?.orderId}`;
                    newSessionLoaded.color = "green";
                    newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}\n\n## Placed order with ID: ${newSessionLoaded?.latestSession?.orderId}`;
                } else if (orderPlaces?.length) {
                    const fromNumber = newSessionLoaded?.from;
                    const otherTransfers = sessionContainer.allAppTransfers.filter(t => t.from === fromNumber);
                    if (otherTransfers?.length) {
                        if (otherTransfers.some(t => {
                            // if transfer eventDate is after session.startDate thenn mark. 
                            const eventDate = new Date(t.eventDate).valueOf();
                            const startDate = session.startDate.valueOf();
                            // compare both dates with epoch times.
                            // needs to be within 1 day. 
                            if (eventDate > startDate && eventDate - startDate < MS_IN_2_HOURS) {
                                return true;
                            }
                            return false;
                        })) {
                            if (filterOrderPlacedTransfers) {
                                add = false;
                            } else {
                                newSessionLoaded.extraText = "Text sent, called back, transferred";
                                newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}\n\n## Payment link sent via text to caller who then called back and transferred.`;
                            }
                        }
                    }
                    if (!newSessionLoaded?.extraText?.length) {
                        newSessionLoaded.extraText = "Order placed, text sent";
                        newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}\n\n## Payment link sent via text to caller.`;

                    }
                } else if (transfers?.length) {
                    newSessionLoaded.extraText = "Transferred";
                    newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}\n\n## Caller transferred`;
                    if (filterTransfers) {
                        add = false;
                    }
                } else {
                    // try to find if there was a transfer from this number. 
                    const fromNumber = newSessionLoaded?.from;
                    const otherTransfers = sessionContainer.allAppTransfers.filter(t => t.from === fromNumber);
                    if (otherTransfers?.length) {
                        if (otherTransfers.some(t => {
                            // if transfer eventDate is after session.startDate thenn mark. 
                            const eventDate = new Date(t.eventDate).valueOf();
                            const startDate = session.startDate.valueOf();
                            // compare both dates with epoch times.
                            // needs to be within 1 day. 
                            if (eventDate > startDate && eventDate - startDate < MS_IN_2_HOURS) {
                                return true;
                            }
                            return false;
                        })) {
                            if (filterRetransfers) {
                                add = false;
                            } else {
                                newSessionLoaded.extraText = "Called back, transferred";
                                newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}\n\n## Caller called back later and transferred`;
                            }
                        }
                    }
                }
                if (add) {
                    if (!newSessionLoaded?.description?.length) {
                        newSessionLoaded.description = `## Call from ${formatFullPhoneNumber(session.from)}`;
                    }
                    return newSessionLoaded;
                }
            }
        } else {
            // no content hits so we're not going to show this session
            return null;
        }
        return null;
    }

    const formatLastFour = (input: string) => {
        // if input contains non numbers
        if (!/^\d+$/.test(input)) {
            return ("Hidden");
        }
        // input: 12345678901
        // output: 8901
        if (input.length != 11) {
            return input;
        }
        const areaCode = input.substring(1, 4);
        const firstThree = input.substring(4, 7);
        const lastFour = input.substring(7, 11);
        return `${lastFour}`;
    }
    const formatFullPhoneNumber = (input: string) => {
        // input: 12345678901
        // output: 8901
        if (!/^\d+$/.test(input)) {
            return ("Hidden");
        }
        if (input.length != 11) {
            return input;
        }
        const areaCode = input.substring(1, 4);
        const firstThree = input.substring(4, 7);
        const lastFour = input.substring(7, 11);
        return `(${areaCode}) ${firstThree}-${lastFour}`;
    }

    const mainRender = () => {
        const loading = (sessionContainer?.loading || loadingSession || !allLoaded || !loadedSessions?.length);
        const loadedSelectedSession = loadedSessions.find(s => s.sessionId === selectedSession);
        const hideFooter = (loading || !loadedSelectedSession?.description?.length);
        return (
            <>
                <StyledHeader>
                    {selectedSession?.length ?
                        <BackButton onClick={() => {
                            props.history.push({
                                search: ``
                            })
                        }} /> : <Logo isFull isInverse linkOverride={`/review/${props.applicationId}`} />}

                    <DashboardHeader noBreadCrumb forceTitle />
                </StyledHeader>
                {
                    showPassword ? <CenterTopDiv>
                        <TextField
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    login();
                                }
                            }}
                            type="password"
                            disabled={props.authContainer.state.isLoading}
                            useForm={false}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            label="Enter Password"
                        />
                        <Button themes={["primary"]} text='Submit' onClick={async () => {
                            login();
                        }}
                        />
                    </CenterTopDiv> :
                        loading ? <Loader />
                            :
                            selectedSession?.length > 0 ?
                                <>
                                    <SessionReview
                                        requireSpeech
                                        manualHeight={(windowSize.windowSize.innerHeight) - (hideFooter ? STATIC_SUBTRACTION_NO_FOOTER : STATIC_SUBTRACTION_SESSION)}
                                        forcedHeight={(windowSize.windowSize.innerHeight) - (hideFooter ? STATIC_SUBTRACTION_NO_FOOTER : STATIC_SUBTRACTION_SESSION)}
                                        events={sessionEvents}
                                        applicationId={props.applicationId}
                                        hideBorder
                                        superSimple
                                        simple
                                        mobileBubbles
                                        setEventsToShow={(e) => null}
                                        resetCopyButtonText={() => null}
                                        userBubbleStyle={UserBubbleStyle}
                                        appContainer={props.appContainer}
                                        onSessionSelect={(sessionId) => {
                                            if (window.location.search?.includes(sessionId)) return;
                                            props.history.push({
                                                search: `?sessionId=${sessionId}`
                                            })
                                        }}
                                    />
                                    {loading || !loadedSelectedSession?.description?.length ? <></> :
                                        <StyledMarkdownFooter>
                                            <ConversationMarkdown conversationText={loadedSelectedSession?.description} />
                                        </StyledMarkdownFooter>
                                    }
                                </>
                                :
                                <StyledSessionList
                                    autoHide
                                    autoHeight
                                    autoHeightMax={(windowSize.windowSize.innerHeight) - STATIC_SUBTRACTION_MAIN}
                                    autoHeightMin={(windowSize.windowSize.innerHeight) - STATIC_SUBTRACTION_MAIN}
                                >
                                    {
                                        <>
                                            <Spacer />
                                            {
                                                filteredLoadedSessions.map((session, idx) => {
                                                    return <SessionPill
                                                        bgColor={session.color}
                                                        key={idx}
                                                        mobile={isMobile}
                                                        onPointerDown={(e) => {
                                                            if (isMobile) return;
                                                            if (window.location.search?.includes(session.sessionId)) return;
                                                            props.history.push({
                                                                search: `?sessionId=${session.sessionId}`
                                                            })
                                                        }}
                                                    >
                                                        <PhoneIcon src={phoneIcon} />
                                                        <PhoneNumber>{formatLastFour(session.from)}</PhoneNumber>
                                                        {session?.orderId?.length ? <OrderIdDiv>Order {session?.orderId}</OrderIdDiv> : <></>}
                                                        {session?.extraText ? <OrderIdDiv>{session?.extraText}</OrderIdDiv> : <></>}
                                                        <TimeDiv>{moment(session.startDate).format(REVIEW_SESSION_DATE_FORMAT)}</TimeDiv>
                                                        {!isMobile ? <></> : <NavigateIcon onPointerDown={(e) => {
                                                            if (window.location.search?.includes(session.sessionId)) return;
                                                            props.history.push({
                                                                search: `?sessionId=${session.sessionId}`
                                                            })
                                                        }} src={expandIcon} />}
                                                    </SessionPill>
                                                })
                                            }
                                            <Spacer />
                                        </>
                                    }
                                </StyledSessionList>
                }
                {(loading || selectedSession?.length) ? <></> :
                    <StyledFooter>
                        <StyledSearchField
                            id="searchField"
                            name={"searchField"}
                            placeholder={"Phone number, order id"}
                            value={searchFieldValue}
                            onChange={(e) => setSearchFieldValue(e.target.value)}
                            boxType={"square"}
                        />
                    </StyledFooter>
                }
                <PageError errors={props.authContainer.state.errors?.length ? props.authContainer.state.errors : []} onClear={() => props.authContainer.clearErrors()} />
            </>
        );
    }
    if (redirect) {
        return (
            <Redirect to="/login" />
        )
    }
    if (isMobile) {
        return (<MainRender>
            {mainRender()}
        </MainRender>)
    }
    return (
        <MobileParent>
            <LeftRightPanel />
            <DesktopMainRender>
                {mainRender()}
            </DesktopMainRender>
            <LeftRightPanel />
        </MobileParent>
    );
}

const PhoneNumber = styled.div`
    font-weight: bold;
    margin-right: 8px;
`
const TimeDiv = styled.div`
    margin-right: 8px;
    margin-left: auto;
`
const OrderIdDiv = styled.div`
`

const NavigateIcon = styled.img`
    marigin-right: 16px;
    cursor: pointer;
`
const PhoneIcon = styled.img`
    margin-right: 4px;
`
const SessionPill = styled.div < { mobile?: boolean, bgColor?: string }>`
    height: 56px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    ${props => props.mobile ? 'margin: 0 16px 16px 16px;' : 'margin: 0 16px 16px 16px;'}    
    padding: 16px;
    ${props => props.mobile ? '' : 'cursor: pointer;'}
    ${props => props.bgColor === "yellow" ? `background: ${pastel_yellow};` : props.bgColor === "green" ? `background: ${pastel_green};` : 'background: white;'}
    &.selected {
        background: ${color_shades_lighter};
    }
    &.last {
        margin-bottom: 32px;
    }
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const MainRender = styled.div`
    width: 100%;
    height: 100%;
`

const DesktopMainRender = styled.div`
    width: 34vw;
`

const LeftRightPanel = styled.div`
    height: 100%;
    background-color: ${color_shades_lighter};
    width: 33vw;
`
const MobileParent = styled.div`
    display: flex;
    flex-direction: row;
`;

const StyledHeader = styled(HeaderBar)`
    width: 100%;
`

const StyledSessionList = styled(CustomScrollbars) < { mobile?: boolean, bgColor?: string }>`
`;
const Spacer = styled.div`
    height: 16px;
`;
const StyledSearchField = styled(SearchField)`  
    margin: 0 8px 0px 8px;
`;
const StyledFooter = styled.div`
    height: 60px;
    width: 100%;
`
const StyledMarkdownFooter = styled.div`
    text-align: center;
    height: 60px;
    width: 100%;
    padding: 16px;
    padding-top: 8px;
    border-top: solid 1px ${greeny_blue};
    h2 {
        color: ${color_shades_darkest} !important;
    }
`
// create a new class name
const UserBubbleStyle = css`
    background: ${color_colors_light_ocean} !important;
`;

const CenterTopDiv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 75%;
    margin-top: 30%;
    margin-left: auto;
    margin-right: auto;
`

export default ReviewDashboardContent;
